import type { FC } from 'react'
import type { CreateRewardDTO, CreateRewardCategoryDTO, RewardCategoryWithRewards } from 'types/rewards'

import { DeleteOutlined, PlusOutlined } from '@ant-design/icons'
import {
  Box,
  Button,
  CircularProgress,
  FormControlLabel,
  Grid2 as Grid,
  IconButton,
  Stack,
  Switch,
  Table,
  TableBody,
  TableCell,
  TableContainer,
  TableHead,
  TableRow,
  Typography,
  useTheme,
} from '@mui/material'

import AnimateButton from 'components/@extended/AnimateButton'
import Paper from 'components/@extended/Paper'
import { TextField } from 'components/@extended/TextField'
import SimpleTitle from 'components/SimpleTitle'
import useAuth from 'hooks/useAuth'
import useLocalStorage from 'hooks/useLocalStorage'
import {
  useFetchCategoriesWithRewards,
  useCreateRewardCategory,
  useBulkCreateRewards,
  invalidateRewardsCache,
} from 'hooks/useRewards'

import { useSnackbar } from 'notistack'
import { startTransition, useCallback, useEffect, useMemo, useState } from 'react'
import { useNavigate } from 'react-router-dom'
import { ThemeMode } from 'types/config'
import { getRootOrganizationId } from 'types/user-profile'
import { CategorySelector } from '../components'

interface RewardMeta {
  name: string
  categoryId: string
  displayOrder: number
  supportsEmployeeContribution: boolean
}

const DEFAULT_REWARD_META: RewardMeta = {
  name: '',
  categoryId: '',
  displayOrder: 100,
  supportsEmployeeContribution: false,
}

const CreateRewards: FC = () => {
  const theme = useTheme()
  const hoverBackgroundColor =
    theme.palette.mode === ThemeMode.DARK ? theme.palette.primary.darker : theme.palette.secondary.dark

  const { enqueueSnackbar } = useSnackbar()
  const navigate = useNavigate()

  const { token, profile } = useAuth()
  const [rootOrganizationId, setRootOrganizationId] = useState<string>('')

  const [rewardMetas, setRewardMetas] = useLocalStorage<RewardMeta[]>(
    'createRewards-rewardMetas',
    [{ ...DEFAULT_REWARD_META }],
    profile?.user?.id
  )

  const [isReviewMode, setIsReviewMode] = useState(false)
  const [isSubmitting, setIsSubmitting] = useState(false)
  const [reviewRewards, setReviewRewards] = useState<CreateRewardDTO[]>([])

  // Fetch categories
  const {
    categoriesWithRewards,
    isLoading: isLoadingCategories,
    refetch: refetchCategories,
  } = useFetchCategoriesWithRewards(rootOrganizationId, token!, !!rootOrganizationId && !!token)

  const { createCategory } = useCreateRewardCategory(token!)
  const { bulkCreateRewards, isLoading: isCreatingRewards } = useBulkCreateRewards(token!)

  // Build category map for lookup
  const categoryMap = useMemo(() => {
    if (!categoriesWithRewards) return {}
    return categoriesWithRewards.reduce(
      (acc, cat) => {
        acc[cat.id!] = cat
        return acc
      },
      {} as Record<string, RewardCategoryWithRewards>
    )
  }, [categoriesWithRewards])

  useEffect(() => {
    if (profile) {
      const orgId = getRootOrganizationId(profile)
      if (orgId) {
        setRootOrganizationId(orgId)
      }
    }
  }, [profile])

  const handleRewardChange = (index: number, field: keyof RewardMeta, value: any) => {
    const newMetas = [...rewardMetas]
    newMetas[index] = { ...newMetas[index], [field]: value }
    setRewardMetas(newMetas)
  }

  const addReward = () => {
    setRewardMetas((prev) => [...prev, { ...DEFAULT_REWARD_META }])
  }

  const removeReward = (index: number) => {
    setRewardMetas((prev) => prev.filter((_, i) => i !== index))
  }

  const createReviewRewards = useCallback(() => {
    if (!rootOrganizationId) return

    const rewards: CreateRewardDTO[] = rewardMetas
      .filter((meta) => meta.name.trim() !== '' && meta.categoryId !== '')
      .map((meta) => ({
        rootOrganizationId: rootOrganizationId,
        organizationId: rootOrganizationId,
        name: meta.name,
        categoryId: meta.categoryId,
        displayOrder: meta.displayOrder,
        supportsEmployeeContribution: meta.supportsEmployeeContribution,
      }))

    setReviewRewards(rewards)
    setIsReviewMode(true)
  }, [rootOrganizationId, rewardMetas])

  const handleReview = () => {
    createReviewRewards()
  }

  const handleCreateRewards = async () => {
    setIsSubmitting(true)
    try {
      const result = await bulkCreateRewards(reviewRewards)

      enqueueSnackbar(`${result.count} rewards created successfully`, { variant: 'success' })

      // Reset form
      setIsReviewMode(false)
      setRewardMetas([{ ...DEFAULT_REWARD_META }])
      invalidateRewardsCache(rootOrganizationId, token!)

      startTransition(() => navigate('/rewards/manage'))
    } catch (error: any) {
      const errorMessage = error.message || 'Failed to create rewards'
      enqueueSnackbar(errorMessage, { variant: 'error' })
    } finally {
      setIsSubmitting(false)
    }
  }

  const handleBackFromReview = () => {
    setIsReviewMode(false)
  }

  const handleClear = () => {
    setRewardMetas([{ ...DEFAULT_REWARD_META }])
    setIsReviewMode(false)
  }

  // Category creation handler for inline creation
  const handleCreateCategory = useCallback(
    async (dto: CreateRewardCategoryDTO) => {
      try {
        const newCategory = await createCategory(dto)
        enqueueSnackbar('Category created successfully', { variant: 'success' })
        invalidateRewardsCache(rootOrganizationId, token!)
        await refetchCategories()
        // Auto-select the newly created category for the last reward row
        if (newCategory?.id) {
          const lastIndex = rewardMetas.length - 1
          handleRewardChange(lastIndex, 'categoryId', newCategory.id)
        }
      } catch (error) {
        enqueueSnackbar('Failed to create category', { variant: 'error' })
        throw error
      }
    },
    [
      createCategory,
      enqueueSnackbar,
      rootOrganizationId,
      token,
      refetchCategories,
      rewardMetas.length,
      handleRewardChange,
    ]
  )

  const renderRewardsConfig = () => (
    <Paper sx={{ p: 3, mb: 2 }}>
      {isLoadingCategories ? (
        <Box display="flex" justifyContent="center" py={4}>
          <CircularProgress />
        </Box>
      ) : (
        <Grid container spacing={3}>
          {rewardMetas.map((rewardMeta, index) => (
            <Grid container size={{ xs: 12 }} spacing={2} key={`reward-meta-${index}`}>
              <Grid size={{ xs: 12, sm: 3 }}>
                <CategorySelector
                  value={rewardMeta.categoryId}
                  onChange={(value) => handleRewardChange(index, 'categoryId', value)}
                  categories={categoriesWithRewards}
                  isLoading={isLoadingCategories}
                  onCreateCategory={handleCreateCategory}
                  organizationId={rootOrganizationId}
                />
              </Grid>
              <Grid size={{ xs: 12, sm: 5 }}>
                <TextField
                  label="Reward Name *"
                  value={rewardMeta.name}
                  onChange={(e) => handleRewardChange(index, 'name', e.target.value)}
                  variant="outlined"
                  pill
                  shrink
                  fullWidth
                />
              </Grid>
              <Grid size={{ xs: 12, sm: 3 }} sx={{ display: 'flex', alignItems: 'center' }}>
                <FormControlLabel
                  control={
                    <Switch
                      checked={rewardMeta.supportsEmployeeContribution}
                      onChange={(e) => handleRewardChange(index, 'supportsEmployeeContribution', e.target.checked)}
                    />
                  }
                  label="Employee Contribution"
                />
              </Grid>
              <Grid size={{ xs: 12, sm: 1 }} sx={{ display: 'flex', justifyContent: 'center', alignItems: 'center' }}>
                <IconButton onClick={() => removeReward(index)} disabled={rewardMetas.length === 1}>
                  <DeleteOutlined />
                </IconButton>
              </Grid>
            </Grid>
          ))}
          <Grid size={{ xs: 12 }} sx={{ display: 'flex', justifyContent: 'flex-start', alignItems: 'center' }}>
            <IconButton color="primary" onClick={addReward} sx={{ width: '250px' }}>
              <Box display="flex" alignItems="center" justifyContent="center">
                <PlusOutlined style={{ marginRight: 8 }} />
                {rewardMetas.length > 0 ? 'Add Another Reward' : 'Add Reward'}
              </Box>
            </IconButton>
          </Grid>
        </Grid>
      )}
    </Paper>
  )

  const renderReviewConfig = () => (
    <>
      <Typography variant="h4" gutterBottom>
        Review Rewards
      </Typography>
      <Paper sx={{ mb: 3 }}>
        <TableContainer>
          <Table stickyHeader size="small" aria-label="review-rewards-table">
            <TableHead
              sx={{
                '& th': {
                  borderTop: `1px solid ${theme.palette.divider}`,
                  borderBottom: `2px solid ${theme.palette.divider} !important`,
                },
              }}
            >
              <TableRow>
                <TableCell width="30%">Category</TableCell>
                <TableCell width="35%">Name</TableCell>
                <TableCell width="10%" align="center">
                  Order
                </TableCell>
                <TableCell width="25%" align="center">
                  Employee Contribution
                </TableCell>
              </TableRow>
            </TableHead>
            <TableBody>
              {reviewRewards.map((reward, index) => (
                <TableRow key={index} sx={{ py: 3, '&:hover': { backgroundColor: hoverBackgroundColor } }}>
                  <TableCell>{categoryMap[reward.categoryId]?.name || reward.categoryId}</TableCell>
                  <TableCell>{reward.name}</TableCell>
                  <TableCell align="center">{reward.displayOrder}</TableCell>
                  <TableCell align="center">{reward.supportsEmployeeContribution ? 'Yes' : 'No'}</TableCell>
                </TableRow>
              ))}
              {reviewRewards.length === 0 && (
                <TableRow>
                  <TableCell colSpan={4} align="center">
                    <Typography color="textSecondary">No rewards to create</Typography>
                  </TableCell>
                </TableRow>
              )}
            </TableBody>
          </Table>
        </TableContainer>
      </Paper>
    </>
  )

  return (
    <>
      <Grid container pb={2}>
        <Grid size={{ xs: 12 }}>
          <SimpleTitle title="Create Rewards" />
        </Grid>
      </Grid>

      {!isReviewMode && (
        <>
          <Box sx={{ display: 'flex', justifyContent: 'flex-end', alignItems: 'center', mb: 3, gap: 2 }}>
            <Button variant="contained" color="error" onClick={handleClear} size="medium" sx={{ minWidth: '80px' }}>
              Clear
            </Button>
          </Box>
          {renderRewardsConfig()}
          <Box sx={{ mb: 3 }}>
            <Stack direction="row" justifyContent="space-between" spacing={2}>
              <AnimateButton>
                <Button
                  variant="contained"
                  color="warning"
                  onClick={() => {
                    startTransition(() => {
                      navigate('/rewards/manage')
                    })
                  }}
                >
                  Cancel
                </Button>
              </AnimateButton>
              <AnimateButton>
                <Button
                  id="review-rewards-button"
                  variant="contained"
                  onClick={handleReview}
                  disabled={isLoadingCategories}
                >
                  Review
                </Button>
              </AnimateButton>
            </Stack>
          </Box>
        </>
      )}

      {isReviewMode && (
        <>
          {renderReviewConfig()}
          <Box display="flex" justifyContent="space-between" sx={{ mb: 3 }}>
            <Stack direction="row" spacing={2}>
              <AnimateButton>
                <Button
                  variant="contained"
                  color="warning"
                  onClick={() => {
                    startTransition(() => {
                      navigate('/rewards/manage')
                    })
                  }}
                >
                  Cancel
                </Button>
              </AnimateButton>
            </Stack>
            <Stack direction="row" spacing={2}>
              <Button variant="outlined" onClick={handleBackFromReview}>
                Back
              </Button>
              <Button
                id="create-rewards-button"
                variant="contained"
                onClick={handleCreateRewards}
                disabled={isSubmitting || isCreatingRewards || reviewRewards.length === 0}
              >
                {isSubmitting || isCreatingRewards ? <CircularProgress size={24} color="inherit" /> : 'Create Rewards'}
              </Button>
            </Stack>
          </Box>
        </>
      )}
    </>
  )
}

export default CreateRewards
